import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { addJobListing, updateJobListing, deleteJobListing, getParentJobs } from '../../services/firestore';

const ParentJobListing = () => {
  const { currentUser, userProfile } = useAuth();
  
  // State for job listings
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for new job form
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requirements: '',
    duration: 'temporary', // 'temporary', 'part-time', 'full-time'
    startDate: '',
    endDate: '',
    schedule: {
      monday: { selected: false, startTime: '', endTime: '' },
      tuesday: { selected: false, startTime: '', endTime: '' },
      wednesday: { selected: false, startTime: '', endTime: '' },
      thursday: { selected: false, startTime: '', endTime: '' },
      friday: { selected: false, startTime: '', endTime: '' },
      saturday: { selected: false, startTime: '', endTime: '' },
      sunday: { selected: false, startTime: '', endTime: '' }
    },
    hourlyRate: '',
    qualifications: [],
    responsibilities: []
  });
  
  // State for editing
  const [editingJobId, setEditingJobId] = useState(null);
  
  // List states
  const [newQualification, setNewQualification] = useState('');
  const [newResponsibility, setNewResponsibility] = useState('');

  // Fetch jobs on component mount
  useEffect(() => {
    if (!currentUser) return;
    
    const fetchJobs = async () => {
      try {
        setLoading(true);
        const jobsList = await getParentJobs(currentUser.uid);
        setJobs(jobsList);
      } catch (error) {
        console.error('Error fetching jobs:', error);
        setError('Failed to load job listings');
      } finally {
        setLoading(false);
      }
    };
    
    fetchJobs();
  }, [currentUser]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle schedule changes
  const handleScheduleChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [day]: {
          ...prev.schedule[day],
          [field]: value
        }
      }
    }));
  };

  // Handle adding/removing qualifications
  const handleAddQualification = () => {
    if (!newQualification.trim()) return;
    setFormData(prev => ({
      ...prev,
      qualifications: [...prev.qualifications, newQualification.trim()]
    }));
    setNewQualification('');
  };

  const handleRemoveQualification = (index) => {
    setFormData(prev => ({
      ...prev,
      qualifications: prev.qualifications.filter((_, i) => i !== index)
    }));
  };

  // Handle adding/removing responsibilities
  const handleAddResponsibility = () => {
    if (!newResponsibility.trim()) return;
    setFormData(prev => ({
      ...prev,
      responsibilities: [...prev.responsibilities, newResponsibility.trim()]
    }));
    setNewResponsibility('');
  };

  const handleRemoveResponsibility = (index) => {
    setFormData(prev => ({
      ...prev,
      responsibilities: prev.responsibilities.filter((_, i) => i !== index)
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) return;

    try {
      const jobData = {
        ...formData,
        parentUid: currentUser.uid,
        parentName: userProfile?.displayName || '',
        providerUid: null,
        createdAt: new Date(),
        status: 'active'
      };

      if (editingJobId) {
        await updateJobListing(editingJobId, jobData);
        setJobs(prev => prev.map(job => 
          job.id === editingJobId ? { ...job, ...jobData } : job
        ));
      } else {
        const newJob = await addJobListing(jobData);
        setJobs(prev => [...prev, newJob]);
      }

      resetForm();
    } catch (error) {
      console.error('Error saving job:', error);
      setError('Failed to save job listing');
    }
  };

  // Handle job deletion
  const handleDelete = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job listing?')) return;

    try {
      await deleteJobListing(jobId);
      setJobs(prev => prev.filter(job => job.id !== jobId));
    } catch (error) {
      console.error('Error deleting job:', error);
      setError('Failed to delete job listing');
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      requirements: '',
      duration: 'temporary',
      startDate: '',
      endDate: '',
      schedule: {
        monday: { selected: false, startTime: '', endTime: '' },
        tuesday: { selected: false, startTime: '', endTime: '' },
        wednesday: { selected: false, startTime: '', endTime: '' },
        thursday: { selected: false, startTime: '', endTime: '' },
        friday: { selected: false, startTime: '', endTime: '' },
        saturday: { selected: false, startTime: '', endTime: '' },
        sunday: { selected: false, startTime: '', endTime: '' }
      },
      hourlyRate: '',
      qualifications: [],
      responsibilities: []
    });
    setEditingJobId(null);
    setShowForm(false);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  // Helper to format date and time separately
  const getDateAndTime = (timestamp) => {
    if (!timestamp) return { date: '', time: '' };
    const dateObj = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    return {
      date: dateObj.toLocaleDateString(),
      time: dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  return (
    <div className="job-listing">
      <h1>Manage Job Listings</h1>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="action-buttons">
        <button 
          className="primary-button"
          onClick={() => {
            resetForm();
            setShowForm(!showForm);
          }}
        >
          {showForm ? 'Cancel' : 'Post New Job'}
        </button>
      </div>
      
      {/* Add/Edit Job Form */}
      {showForm && (
        <div className="job-form-container form-section">
          <h2>{editingJobId ? 'Edit Job' : 'Post New Job'}</h2>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="title">Job Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Part-time Nanny, Early Childhood Educator"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Job Description *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe the job role and expectations"
                rows={4}
                required
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="duration">Duration *</label>
                <select
                  id="duration"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  required
                >
                  <option value="temporary">Temporary</option>
                  <option value="part-time">Part-time</option>
                  <option value="full-time">Full-time</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="hourlyRate">Hourly Rate ($)</label>
                <input
                  type="number"
                  id="hourlyRate"
                  name="hourlyRate"
                  value={formData.hourlyRate}
                  onChange={handleChange}
                  placeholder="Enter hourly rate"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startDate">Start Date</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="endDate">End Date</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  min={formData.startDate}
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>Schedule</label>
              <div className="schedule-grid">
                {Object.entries(formData.schedule).map(([day, schedule]) => (
                  <div key={day} className="schedule-day">
                    <label>
                      <input
                        type="checkbox"
                        checked={schedule.selected}
                        onChange={(e) => handleScheduleChange(day, 'selected', e.target.checked)}
                      />
                      {day.charAt(0).toUpperCase() + day.slice(1)}
                    </label>
                    {schedule.selected && (
                      <div className="schedule-times">
                        <input
                          type="time"
                          value={schedule.startTime}
                          onChange={(e) => handleScheduleChange(day, 'startTime', e.target.value)}
                        />
                        <span>to</span>
                        <input
                          type="time"
                          value={schedule.endTime}
                          onChange={(e) => handleScheduleChange(day, 'endTime', e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="form-group">
              <label>Qualifications</label>
              <div className="list-input">
                <input
                  type="text"
                  value={newQualification}
                  onChange={(e) => setNewQualification(e.target.value)}
                  placeholder="Add a qualification"
                />
                <button type="button" onClick={handleAddQualification}>Add</button>
              </div>
              <ul className="list-items">
                {formData.qualifications.map((qualification, index) => (
                  <li key={index}>
                    {qualification}
                    <button type="button" onClick={() => handleRemoveQualification(index)}>×</button>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="form-group">
              <label>Responsibilities</label>
              <div className="list-input">
                <input
                  type="text"
                  value={newResponsibility}
                  onChange={(e) => setNewResponsibility(e.target.value)}
                  placeholder="Add a responsibility"
                />
                <button type="button" onClick={handleAddResponsibility}>Add</button>
              </div>
              <ul className="list-items">
                {formData.responsibilities.map((responsibility, index) => (
                  <li key={index}>
                    {responsibility}
                    <button type="button" onClick={() => handleRemoveResponsibility(index)}>×</button>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="form-group">
              <label htmlFor="requirements">Additional Requirements</label>
              <textarea
                id="requirements"
                name="requirements"
                value={formData.requirements}
                onChange={handleChange}
                placeholder="Any additional requirements or notes"
                rows={3}
              />
            </div>
            
            <div className="form-actions">
              <button type="submit" className="primary-button">
                {editingJobId ? 'Update Job' : 'Post Job'}
              </button>
              <button type="button" className="secondary-button" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Job Listings */}
      <div className="jobs-list">
        {loading ? (
          <div className="loading-container">
            <p>Loading job listings...</p>
          </div>
        ) : jobs.length > 0 ? (
          jobs.map(job => {
            const { date, time } = getDateAndTime(job.createdAt);
            return (
              <div className="job-card" key={job.id}>
                <div className="job-header">
                  <h3>{job.title}</h3>
                  <span className={`job-type ${job.duration}`}>
                    {job.duration ? job.duration.charAt(0).toUpperCase() + job.duration.slice(1) : 'Temporary'}
                  </span>
                </div>
                <div className="job-details">
                  <div className="job-info">
                    <p><strong>Posted Date:</strong> {date}</p>
                    <p><strong>Posted Time:</strong> {time}</p>
                    <p><strong>Start Date:</strong> {formatDate(job.startDate)}</p>
                    {job.endDate && <p><strong>End Date:</strong> {formatDate(job.endDate)}</p>}
                    <p><strong>Rate:</strong> ${job.hourlyRate}/hour</p>
                  </div>
                  <div className="job-actions">
                    <button 
                      onClick={() => {
                        setFormData(job);
                        setEditingJobId(job.id);
                        setShowForm(true);
                      }}
                      className="btn btn-outline btn-sm"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDelete(job.id)}
                      className="btn btn-danger btn-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="empty-state">
            <p>No job listings posted yet.</p>
            <button onClick={() => setShowForm(true)} className="btn btn-primary">
              Post a Job
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentJobListing; 